import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    FormControlLabel,
    Checkbox,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useGetList, useNotify } from 'react-admin';
import { APIUrl } from '../../common/apiConfig';

/**
 * A teacher's class assignments -- which is to say, everything they can see.
 *
 * THIS IS NOT A LIST OF DUTIES
 * ----------------------------
 * `teacher_assignment` is the authorisation scope. Every row added here widens
 * what this person can read; every row removed narrows it. A teacher with no
 * rows can read NOTHING -- not their subject across the school, not the student
 * list, nothing at all.
 *
 * That is the confirmed decision of 2026-09-10 and it is why the empty state
 * here is a warning rather than a neutral "none yet". An administrator who
 * imports a roster and stops has created accounts that can log in and then find
 * a blank product, and the only place that is visible is this panel.
 *
 * Withdrawing takes effect on the teacher's next REQUEST, not their next login:
 * the scope is read per request and never cached into the token. Removing a
 * whole membership is the case that needs a token revoked, and the API does
 * that itself.
 */

type Assignment = {
    id: number;
    class_section_id: number;
    section_label: string;
    academic_year: string;
    subject_id: number | null;
    subject_name: string;
    is_class_teacher: boolean;
};

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('jwt_access_token')}`,
});

export const AssignmentPanel = ({
    teacherId,
    assignments,
    onChange,
}: {
    teacherId: number;
    assignments: Assignment[];
    onChange: (next: Assignment[]) => void;
}) => {
    const notify = useNotify();
    const [sectionId, setSectionId] = useState<number | ''>('');
    const [subjectId, setSubjectId] = useState<number | ''>('');
    const [classTeacher, setClassTeacher] = useState(false);
    const [busy, setBusy] = useState(false);

    const { data: sections = [] } = useGetList('class-section', {
        pagination: { page: 1, perPage: 200 },
        sort: { field: 'class_name', order: 'ASC' },
    });
    const { data: subjects = [] } = useGetList('subject', {
        pagination: { page: 1, perPage: 200 },
        sort: { field: 'subject', order: 'ASC' },
    });

    const call = async (path: string, body: any) => {
        setBusy(true);
        try {
            const res = await fetch(`${APIUrl}school-teacher/${path}/${teacherId}`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(body),
            });
            const json = await res.json();
            if (json.status !== 'success') {
                notify(json.message || 'That did not work.', { type: 'error' });
                return;
            }
            onChange(json.result.assignments);
            notify(json.message, { type: 'success' });
        } catch (e: any) {
            notify(e.message || 'That did not work.', { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const add = () => {
        if (sectionId === '') {
            notify('Choose a class section first.', { type: 'warning' });
            return;
        }
        call('assign', {
            class_section_id: sectionId,
            subject_id: subjectId === '' ? null : subjectId,
            is_class_teacher: classTeacher ? 1 : 0,
        });
        setSubjectId('');
        setClassTeacher(false);
    };

    return (
        <Card variant="outlined" sx={{ width: '100%', mb: 3 }}>
            <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                    What this teacher can see
                </Typography>

                {assignments.length === 0 ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This teacher has no class assignment, so they can see <b>nothing</b> &mdash;
                        no students, no sections, an empty product. Assign them at least one section
                        below.
                    </Alert>
                ) : (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        They can see {assignments.length} section
                        {assignments.length === 1 ? '' : 's'} and the students in
                        {assignments.length === 1 ? ' it' : ' them'}. Nothing else.
                    </Alert>
                )}

                {assignments.length > 0 && (
                    <Table size="small" sx={{ mb: 2 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Section</TableCell>
                                <TableCell>Subject</TableCell>
                                <TableCell>Year</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {assignments.map(a => (
                                <TableRow key={a.id}>
                                    <TableCell>
                                        <b>{a.section_label}</b>{' '}
                                        {a.is_class_teacher && (
                                            <Chip size="small" label="class teacher" />
                                        )}
                                    </TableCell>
                                    <TableCell>{a.subject_name}</TableCell>
                                    <TableCell>{a.academic_year}</TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            color="error"
                                            disabled={busy}
                                            onClick={() =>
                                                call('unassign', { assignment_id: a.id })
                                            }
                                        >
                                            Withdraw
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Select
                        size="small"
                        displayEmpty
                        value={sectionId}
                        onChange={e => setSectionId(e.target.value as number)}
                        sx={{ minWidth: 180 }}
                    >
                        <MenuItem value="">
                            <em>Class section</em>
                        </MenuItem>
                        {sections.map((s: any) => (
                            <MenuItem key={s.id} value={s.id}>
                                {s.label} {s.stream ? `(${s.stream})` : ''}
                            </MenuItem>
                        ))}
                    </Select>

                    <Select
                        size="small"
                        displayEmpty
                        value={subjectId}
                        onChange={e => setSubjectId(e.target.value as number)}
                        sx={{ minWidth: 180 }}
                    >
                        {/* Empty means every subject in that section. It is the
                            common case for a class teacher, so it is first. */}
                        <MenuItem value="">
                            <em>All subjects</em>
                        </MenuItem>
                        {subjects.map((s: any) => (
                            <MenuItem key={s.id} value={s.id}>
                                {s.subject}
                            </MenuItem>
                        ))}
                    </Select>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={classTeacher}
                                onChange={e => setClassTeacher(e.target.checked)}
                            />
                        }
                        label="Class teacher"
                    />

                    <Button variant="contained" onClick={add} disabled={busy}>
                        Assign
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};
